import { useCallback, useEffect, useState } from 'react'
import { Chip } from '@mui/material'
import Notiflix from 'notiflix'
import { useTranslation } from 'react-i18next'
import styles from './Payment.module.scss'
import instance from '../../../../../../../providers/axios.config'
import { PaymentHistoryTable } from '../../../../../../shared-components/paymentHistory/PaymentHistoryTable'
import { IPaymentHistory, typeResponse } from '../../../../../../../utils/types'
import {
  APPROVED,
  CONFIRMED,
  DENIED,
  PAID,
  WAITING,
} from '../../../../../../../utils/constants'

/* -------------------------------------------------------------------------- */
/* Shared helper consumed by PaymentHistoryTable                              */
/* -------------------------------------------------------------------------- */

export const setTypeStatus = (status: string, label: string): JSX.Element | null => {
  switch (status) {
    case WAITING:
      return <Chip label={label} sx={{ color: 'orange' }} size="small" />
    case DENIED:
      return <Chip label={label} color="primary" size="small" />
    case APPROVED:
    case PAID:
    case CONFIRMED:
      return <Chip label={label} sx={{ color: 'green' }} size="small" />
    default:
      return null
  }
}

/* -------------------------------------------------------------------------- */
/* Types                                                                       */
/* -------------------------------------------------------------------------- */

interface HistoryResponse extends typeResponse {
  data: {
    tax_forms: IPaymentHistory[]
    pagination: {
      currentPage: number
      perPage: number
      totalPages: number
    }
  }
}

interface PaginationState {
  currentPage: number
  perPage: number
  totalPages: number
}

const INITIAL_PAGINATION: PaginationState = {
  currentPage: 1,
  perPage: 10,
  totalPages: 1,
}

/* -------------------------------------------------------------------------- */
/* Component                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Read-only listing of the seller's past payout requests.
 *
 * The request-payout FORM lives in the sibling Payment.tsx (single
 * source of truth — that component already has Redux access to the
 * seller's saved per-method payout accounts (PayPal / Payoneer /
 * Skrill / Revolut) for auto-fill). This file intentionally holds
 * no form state to avoid duplicating business rules across two
 * screens.
 */
export const PaymentHistory = (): JSX.Element => {
  const { t } = useTranslation()

  const [historyLoading, setHistoryLoading] = useState<boolean>(false)
  const [history, setHistory] = useState<IPaymentHistory[]>([])
  const [pagination, setPagination] = useState<PaginationState>(INITIAL_PAGINATION)

  const fetchHistory = useCallback(async (page: number): Promise<void> => {
    setHistoryLoading(true)
    try {
      const res = await instance.get(
        `seller/tax-center/payment-history?limit=${pagination.perPage}&page=${page}`
      ) as HistoryResponse

      setHistory(res?.data?.tax_forms ?? [])
      if (res?.data?.pagination) {
        setPagination((prev) => ({
          ...prev,
          ...res.data.pagination,
        }))
      }
    } catch {
      Notiflix.Report.failure(t('error'), t('failed_to_fetch_history'), 'OK')
    } finally {
      setHistoryLoading(false)
    }
  }, [pagination.perPage, t])

  useEffect(() => {
    fetchHistory(pagination.currentPage)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.currentPage])

  const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number): void => {
    setPagination((prev) => ({ ...prev, currentPage: page }))
  }

  return (
    <div className={styles.tableHistory}>
      <PaymentHistoryTable
        isLoading={historyLoading}
        history={history}
        onChange={handlePageChange}
        pagination={pagination}
      />
    </div>
  )
}
